import { Alert, Button, Descriptions, Modal, Select, Typography, message } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { platformListPlanCatalog, platformUpgradeWorkspacePlan } from '../api'
import type { AdminPlanUpgradeResult, PlanCatalogRow } from '../types'
import { formatBytes } from './adminShared'
import { planUpgradeCurrentLabel, type PlanCatalogLoadState } from './planUpgradeCurrentLabel'

/**
 * Stage S4 — ADMIN_PLAN_UPGRADE flow.
 * Target keys still follow backend ALLOWED_UPGRADE_TRANSITIONS
 * (free→standard/premium, standard→premium). Display names come from the
 * live Plan Catalog — never a hardcoded Chinese name map.
 *
 * TASK-00668: null plan_key → 未配置 (not fake 免费版).
 * TASK-00669: catalog LOADING must not flash 套餐异常; prefer knownDisplayName.
 */

const PLAN_ORDER = ['free', 'standard', 'premium']

function allowedTargets(currentPlanKey: string | null): string[] {
  // Backend treats null plan_key as free for transition checks only.
  const idx = PLAN_ORDER.indexOf(currentPlanKey ?? 'free')
  const effectiveIdx = idx === -1 ? 0 : idx
  return PLAN_ORDER.slice(effectiveIdx + 1)
}

function mintAttemptId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `attempt-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export default function PlanUpgradeModal({
  open,
  onClose,
  workspaceId,
  workspaceName,
  currentPlanKey,
  currentPlanDisplayName = null,
  creditBalance,
  storageQuotaBytes,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  workspaceId: string
  workspaceName: string
  currentPlanKey: string | null
  /** Trusted name from list/detail — shown immediately while catalog loads. */
  currentPlanDisplayName?: string | null
  creditBalance: string
  storageQuotaBytes: number
  onSuccess: (result: AdminPlanUpgradeResult) => void
}) {
  const [plans, setPlans] = useState<PlanCatalogRow[]>([])
  const [catalogState, setCatalogState] = useState<PlanCatalogLoadState>('loading')
  const [target, setTarget] = useState<string | undefined>(undefined)
  const [attemptId, setAttemptId] = useState(mintAttemptId())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadSeq = useRef(0)

  const loadCatalog = () => {
    const seq = ++loadSeq.current
    setCatalogState('loading')
    setError(null)
    platformListPlanCatalog()
      .then((r) => {
        if (seq !== loadSeq.current) return
        setPlans(Array.isArray(r.data) ? r.data : [])
        setCatalogState('ready')
      })
      .catch((err) => {
        if (seq !== loadSeq.current) return
        setPlans([])
        setCatalogState('error')
        setError((err as Error).message || '套餐目录加载失败')
      })
  }

  useEffect(() => {
    if (!open) return
    setAttemptId(mintAttemptId())
    setTarget(undefined)
    loadCatalog()
    return () => {
      // Invalidate in-flight responses when closing or switching tenant.
      loadSeq.current += 1
    }
  }, [open, workspaceId])

  const targets = useMemo(() => allowedTargets(currentPlanKey), [currentPlanKey])

  useEffect(() => {
    if (open && catalogState === 'ready') setTarget(targets[0])
  }, [open, catalogState, targets.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  const currentPlan = plans.find((p) => p.key === currentPlanKey)
  const targetPlan = plans.find((p) => p.key === target)
  const currentLabel = planUpgradeCurrentLabel(currentPlanKey, currentPlan?.display_name, {
    knownDisplayName: currentPlanDisplayName,
    catalogState,
  })

  const catalogReady = catalogState === 'ready'
  // Orphan only after catalog is READY and key has no row (not while loading).
  const isOrphan =
    catalogReady && Boolean(currentPlanKey) && !currentPlan && !currentPlanDisplayName?.trim()
  const canConfirm =
    catalogReady && !isOrphan && Boolean(target) && targets.length > 0 && !submitting

  const balanceNum = Number(creditBalance)
  const grantNum = targetPlan ? Number(targetPlan.credit_grant) : 0
  const afterBalance = balanceNum + grantNum
  const afterStorage = targetPlan ? Math.max(storageQuotaBytes, targetPlan.base_storage_bytes) : storageQuotaBytes
  const storageChanges = afterStorage !== storageQuotaBytes

  const onConfirm = async () => {
    if (!canConfirm || !target) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await platformUpgradeWorkspacePlan(workspaceId, {
        target_plan_key: target,
        upgrade_attempt_id: attemptId,
      })
      message.success(`已将「${workspaceName}」升级为 ${targetPlan?.display_name ?? target}`)
      onSuccess(res.data)
      onClose()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={`升级套餐 — ${workspaceName}`}
      open={open}
      onCancel={onClose}
      onOk={onConfirm}
      okText="确认升级"
      cancelText="取消"
      confirmLoading={submitting}
      okButtonProps={{ disabled: !canConfirm }}
    >
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 12 }}
          action={
            catalogState === 'error' ? (
              <Button size="small" onClick={loadCatalog}>
                重试
              </Button>
            ) : undefined
          }
        />
      )}
      {targets.length === 0 && catalogReady ? (
        <Alert type="info" showIcon message="该租户已是最高套餐（高级版），无可用升级路径。" />
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <Typography.Text type="secondary">目标套餐</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={target}
              onChange={setTarget}
              disabled={!catalogReady}
              placeholder={catalogState === 'loading' ? '正在加载套餐…' : undefined}
              options={targets.map((key) => {
                const p = plans.find((pl) => pl.key === key)
                return { value: key, label: p?.display_name ?? key }
              })}
            />
          </div>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="当前套餐">{currentLabel}</Descriptions.Item>
            <Descriptions.Item label="目标套餐">
              {catalogState === 'loading'
                ? '正在获取…'
                : catalogState === 'error'
                  ? '—'
                  : (targetPlan?.display_name ?? '—')}
            </Descriptions.Item>
            <Descriptions.Item label="积分变化">
              {catalogReady
                ? `当前 ${balanceNum} + 套餐赠送 ${grantNum} = ${afterBalance}`
                : '等待套餐目录加载…'}
            </Descriptions.Item>
            <Descriptions.Item label="存储变化">
              {catalogReady
                ? storageChanges
                  ? `${formatBytes(storageQuotaBytes)} → ${formatBytes(afterStorage)}`
                  : `保持 ${formatBytes(storageQuotaBytes)}（目标套餐基础配额未超过当前配额）`
                : '等待套餐目录加载…'}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Modal>
  )
}
